<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import ExternalIcon from '../design-system/icons/ExternalIcon.vue';
import { globalKeys } from '~/assets/data/global-keys';
import type { IPromotionGenericWheelData } from '~/interfaces/dto/promotions/response';
import { loginRoutes, registerRoutes } from 'assets/data/config';
import type { LoggedIn, LoggedOut } from '~/interfaces/dto/config/response';
import { lockBodyScroll, unlockBodyScroll } from '~/utilities/ui-utilities';
import type {
  IHamburgerMenuState,
  LayoutActiveStates,
} from '~/interfaces/components/layout';
import { useNotifications } from '~/composables/useStates/useNotifications';
import ChatIcon from '../design-system/icons/ChatIcon.vue';

const auth = useAuthStore();
const settings = useSiteStore();
const route = useRoute();
const { $storageService } = useServices();

const hamburgerMenuState = inject<IHamburgerMenuState>('hamburgerMenuState');
const layoutActiveStates = inject<LayoutActiveStates>('activeStates');

const { panelOpen, notificationsState } = useNotifications();

const closedPopUps = ref<IPromotionGenericWheelData[]>([]);
const userAgent = computed(() => navigator.userAgent);
const chromeIos = computed(() => {
  return (
    userAgent.value.includes('CriOS/') && userAgent.value.includes('iPhone;')
  );
});

const currentSpin = computed<IPromotionGenericWheelData>(
  () => auth?.getCurrentSpin,
);

const isPromoInSessionStorage = computed(() =>
  closedPopUps.value.some(
    (promotion) => promotion.promotionId === currentSpin?.value?.promotionId,
  ),
);

const showSpinButton = computed(
  () =>
    closedPopUps.value.length > 0 &&
    !!currentSpin.value &&
    !route?.path?.includes(currentSpin?.value?.redirectUrl) &&
    auth.loggedIn,
);

const hideFooter = computed(
  () => loginRoutes.includes(route.path) || registerRoutes.includes(route.path),
);

const mobileNavigation = computed(() =>
  settings?.config?.devConfig?.footerMenu?.flatMap((menuOption) => {
    if (auth.loggedIn) {
      if (
        showSpinButton.value &&
        (menuOption.loggedIn.Text.toLowerCase() === 'promotions' ||
          menuOption.loggedIn.Text.toLowerCase() === 'promos')
      )
        return {
          Action: 'NavigateTo',
          Value: `${auth?.getCurrentSpin?.redirectUrl}`,
          Text: 'jpc-spin-now',
        };
      else return menuOption.loggedIn;
    }
    if (!!menuOption.loggedOut) return menuOption.loggedOut;
    return [];
  }),
);

const handlePromoSeen = (promo: IPromotionGenericWheelData) => {
  closedPopUps.value = [
    ...(closedPopUps.value as IPromotionGenericWheelData[]),
    promo,
  ];
};

function handleItemClick(item: LoggedIn | LoggedOut) {
  switch (item.Value.toLowerCase()) {
    case 'notifications':
      settings.toggleFeature({ feature: 'displayNotifications' });
      panelOpen.value = !panelOpen.value;
      break;
    case 'menu':
      toggleMobileHamburger();
      break;
    case 'providers':
      if (route.path === '/home/providers') break;
      layoutActiveStates.providers = !layoutActiveStates.providers;
      break;
    default:
      useMessageHandler({ action: item.Action, actionValue: item.Value });
      break;
  }
}

const toggleMobileHamburger = () => {
  hamburgerMenuState.drawerTransition = {
    enterFromClass: 'translate-y-full',
    enterToClass: 'translate-y-0',
    leaveFromClass: 'translate-y-0',
    leaveToClass: 'translate-y-full',
  };
  hamburgerMenuState.active = !hamburgerMenuState?.active;
};

onMounted(() => {
  const sessionClosedPopUps = $storageService.getSessionStorage<
    IPromotionGenericWheelData[]
  >({ key: globalKeys.marketingKeys.closedPromoPopUps });
  if (!!sessionClosedPopUps) closedPopUps.value = [...sessionClosedPopUps];
});

watch(
  () => layoutActiveStates.providers,
  (val) => {
    if (val) lockBodyScroll(true, false);
    else unlockBodyScroll();
  },
);
</script>
<template>
  <div>
    <div
      v-if="mobileNavigation?.length > 0 && !hideFooter"
      class="flex flex-col md:hidden fixed bottom-0 left-0 w-full max-w-screen z-[7] gap-4"
    >
      <div v-if="!isPromoInSessionStorage" class="px-2 slide-in-fwd">
        <LazyUserInterfaceSpinPopUp
          @spin-now-pop-up-viewed="handlePromoSeen($event)"
        />
      </div>
      <div v-if="$enabled('website.livechat')" class="w-full px-4">
        <DesignSystemButton
          shape="square"
          class="h-10 w-10 ml-auto"
          @click="$toggleChat()"
        >
          <ChatIcon />
        </DesignSystemButton>
      </div>
      <div
        id="mobile-footer"
        class="flex justify-between bg-layer"
        :class="{ 'pb-4': chromeIos }"
      >
        <div
          v-for="(item, index) in mobileNavigation"
          :key="index"
          :class="[
            { 'bg-primary-blue-gradient': item.Value.toLowerCase() === 'menu' },
            'w-full py-1.5 cursor-pointer flex flex-col items-center gap-2 relative',
          ]"
          @click="handleItemClick(item)"
        >
          <ExternalIcon
            :icon-name="item?.Text"
            class="mx-auto h-6 w-6"
            :themed="item.Value.toLowerCase() !== 'menu'"
          />
          <span
            v-if="
              item?.Text === 'inbox' &&
              (!!notificationsState?.allNotifications?.unread ||
                settings?.unreadNotificationsCount > 0)
            "
            :class="[
              'text-xxs',
              'badge absolute bg-error-500 rounded-full top-5 right-1/4 sm:right-1/3 text-light-50 w-4 h-4 flex-center font-bold',
            ]"
          >
            {{
              notificationsState?.allNotifications?.unread ||
              settings?.unreadNotificationsCount
            }}
          </span>

          <span
            v-if="item.Text === 'jpc-spin-now'"
            class="badge absolute bg-blue-500 rounded-full top-6 right-6 text-light-50 w-4 h-4 flex items-center"
          >
            <span
              class="mx-auto text-center w-full font-bold text-xs tracking-wide"
            >
              {{ auth.getCurrentSpin?.unusedSpinCount }}
            </span>
          </span>
          <div
            :class="[
              'text-base-priority',
              'text-[9px] text-center font-bold capitalize',
            ]"
          >
            {{ $t(item.Text) }}
          </div>
        </div>
      </div>
    </div>
    <LazyCategoryProviderGrid
      v-if="layoutActiveStates.providers"
      @close-provider-grid="() => (layoutActiveStates.providers = false)"
    />
  </div>
</template>
